import $i18n from '@alife/panda-i18n';
import React, { useCallback, useState, useMemo } from 'react';
import { CnIcon, CnMessage } from '@cainiaofe/cn-ui';
import classnames from 'classnames';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { Bubble } from '@chatui/core';
import './index.scss';
import htmlToPlainText from '@/utils/html-to-plain-text';
import Markdown from '../markdown';

const SUPPORT_STATUS = {
  LIKED: 1,
  UNLIKED: -1,
  NONE: 0,
};

const MessageItem = (props) => {
  const {
    message,
    text,
    code = '',
    type = 'text',
    data = {},
    renderContent,
    noBubble = false,
    textArray = [],
    recommendTitle,
    recommendList = [],
    recommendTotalRows = 0,
    recommendPageSize = 3,
    showChangeRecommend,
    _id,
    support = SUPPORT_STATUS.NONE,
    position,
    loading,
    waiting,
    isLastAnswer,
    onContinue,
    onRetry,
    onSupport,
    onClickRecommend,
    onChangeRecommend,
    onCardEvent,
    showRetry,
    showContinue,
    showSupport,
    showCopy,
    formatUserMessage,
    format,
    messageActions = [],
    messageIcons = [],
    showActions,
    showIcons,
    onClickMessageAction,
    onClickMessageIcon,
    onCopy,
  } = props;
  const sortedText = textArray
    .sort((a, b) => a.timeStamp - b.timeStamp)
    .map((i) => i.text)
    .join('');
  const renderText = useMemo(() => {
    const result = (sortedText || text || '').replace(/\\n/g, '\n').replace(/<br>/g, '\n');
    const imgInfo = data.url_list ? `\n\n![IMG](${data.url_list[0]})` : '';
    return result + imgInfo;
  }, [sortedText, text, data.url_list]);
  const showSupportBubble = position === 'left' && showSupport;
  const handleLike = useCallback(() => {
    onSupport(support === SUPPORT_STATUS.LIKED ? SUPPORT_STATUS.NONE : SUPPORT_STATUS.LIKED);
  }, [support, onSupport]);
  const handleUnlike = useCallback(() => {
    onSupport(support === SUPPORT_STATUS.UNLIKED ? SUPPORT_STATUS.NONE : SUPPORT_STATUS.UNLIKED);
  }, [support, onSupport]);
  const showRecommend = recommendTitle && recommendList.length > 0;
  const [recommendPageIndex, setRecommendPageIndex] = useState(1);
  // const [isActionActive, setIsActionActive] = useState(false);
  const handleChangeRecommend = useCallback(() => {
    let nextPageIndex = recommendPageIndex + 1;
    if (nextPageIndex * recommendPageSize > recommendTotalRows) {
      nextPageIndex = 1;
    }
    onChangeRecommend({
      id: _id,
      pageNum: nextPageIndex,
      pageSize: recommendPageSize,
    });
    setRecommendPageIndex(nextPageIndex);
  }, [onChangeRecommend, recommendPageIndex, _id]);

  const content = useMemo(() => {
    if (_id === 'error' || (position === 'right' && !formatUserMessage && !format)) {
      return (
        <div>
          {renderText.split('\n').map((row) => (
            <div>{row}</div>
          ))}
        </div>
      );
    }
    if (typeof renderContent === 'function') {
      return renderContent(message);
    } else if (type === 'card' && code) {
      const codeAry = code.split('.');
      const CardComponent = codeAry[1] ? window[codeAry[0]]?.[codeAry[1]] : window[codeAry[0]];
      const msgEventData = {
        id: _id,
        text,
        type,
        code,
        data,
      };
      if (CardComponent) {
        return (
          <CardComponent
            loading={loading}
            waiting={waiting}
            text={text}
            data={data}
            onCardEvent={(eventName, params) => {
              onCardEvent(eventName, params, msgEventData);
            }}
            Markdown={Markdown}
          />
        );
      }
      return (
        <div>
          {$i18n.get(
            {
              id: 'ComponentCodeNotFoundPleaseCheck_1275007885',
              dm: '未找到组件{code}，请检查是否正确加载',
            },
            { code },
          )}
        </div>
      );
    }
    return <Markdown text={renderText} />;
  }, [renderText, text, _id, type, code, data, onCardEvent, position]);

  const card = (
    <div className="message">
      {content}
      {showRecommend ? (
        <div className="recommend">
          <div className="recommendTitle">
            <div>{recommendTitle}</div>
            {showChangeRecommend !== false ? (
              <div className="recommendAction" onClick={handleChangeRecommend}>
                <CnIcon size={16} type="refresh" style={{ marginRight: 4 }} />
                <div>{$i18n.get({ id: 'TERM.Refresh', dm: '换一批' })}</div>
              </div>
            ) : null}
          </div>
          <div className="recommendList">
            {recommendList.map((item) => (
              <div
                className="recommendItem"
                onClick={() => {
                  onClickRecommend({
                    ...item,
                    answerId: _id,
                  });
                }}
              >
                <div className="recommendIcon">
                  <img
                    src="https://gw.alicdn.com/imgextra/i2/O1CN01rNO7b01R5WnBo9oml_!!6000000002060-2-tps-24-24.png"
                    className="recommendImg"
                  />
                </div>
                <div>{item.label}</div>
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );

  return (
    <div className={classnames('chatai-ui-message-wrap', position)}>
      {noBubble ? card : <Bubble content={card} />}
      {_id !== 'error' ? (
        <div
          className={classnames('actions', {
            show: showActions,
          })}
        >
          <div className="leftActions">
            {isLastAnswer && showRetry ? (
              <div className={classnames('leftButton', { disabled: loading })} onClick={onRetry}>
                <CnIcon type="refresh" className="icon" />
                <div>{$i18n.get({ id: 'Retry', dm: '重试' })}</div>
              </div>
            ) : null}
            {isLastAnswer && showContinue ? (
              <div className={classnames('leftButton', { disabled: loading })} onClick={onContinue}>
                <CnIcon type="play-r" className="icon" />
                <div>{$i18n.get({ id: 'Continue', dm: '继续' })}</div>
              </div>
            ) : null}
            {position === 'left' && showActions
              ? messageActions.map((i) => (
                  <div
                    className={classnames('leftButton', { disabled: loading })}
                    onClick={() => {
                      onClickMessageAction(i);
                    }}
                  >
                    <CnIcon type={i.icon} v-if={i.icon} style={{ marginRight: 4 }} />
                    <div>{i.label}</div>
                  </div>
                ))
              : null}
          </div>
          {showCopy || showSupportBubble || (showIcons && messageIcons.length > 0) ? (
            <div className={classnames('rightActions', { disabled: loading })}>
              {showCopy ? (
                <CopyToClipboard
                  text={htmlToPlainText(data?.copyText || renderText)}
                  onCopy={() => {
                    CnMessage.success($i18n.get({ id: 'TERM.CopySuccessful', dm: '复制成功' }));
                    onCopy();
                  }}
                >
                  <div className="rightButton">
                    <CnIcon type="copy" />
                    {showSupport ? null : (
                      <div style={{ marginLeft: 4, fontSize: 12 }}>
                        {$i18n.get({ id: 'TERM.Copy', dm: '复制' })}
                      </div>
                    )}
                  </div>
                </CopyToClipboard>
              ) : null}
              {showSupportBubble ? (
                <>
                  <div
                    className={classnames('rightButton', {
                      filled: support === SUPPORT_STATUS.LIKED,
                    })}
                    onClick={handleLike}
                  >
                    <CnIcon type={support === SUPPORT_STATUS.LIKED ? 'good-fill' : 'good'} />
                  </div>
                  <div
                    className={classnames('rightButton', {
                      filled: support === SUPPORT_STATUS.UNLIKED,
                    })}
                    onClick={handleUnlike}
                  >
                    <CnIcon
                      type={support === SUPPORT_STATUS.UNLIKED ? 'good-fill' : 'good'}
                      className="unlike"
                    />
                  </div>
                </>
              ) : null}
              {position === 'left' && showIcons
                ? messageIcons.map((i) => (
                    <div
                      className="rightButton"
                      onClick={() => {
                        onClickMessageIcon(i);
                      }}
                    >
                      <CnIcon type={i.icon} />
                    </div>
                  ))
                : null}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

export default MessageItem;
